import { useRef } from "react";
import { Box, Divider, useTheme, CircularProgress } from "@mui/material";
import useScreenSize from "@/modules/common/hooks/device-detection.hook";
import AvatarWithConnectors from "@/modules/targetsv2/components/avatar-with-connectors";
import TargetsActions from "@/modules/targetsv2/components/targets-actions/targets-actions";
import { useTargetsPage } from "@/modules/targetsv2/hooks/targets-page.hook";
import EmptySection from "@/modules/common/components/ui/empty-section";

const Targets2 = () => {
  const screen = useScreenSize();

  const {
    selectedYear,
    selectedQuarter,
    selectedUserUId,
    handleYearChange,
    handleQuarterChange,
    handleUserChange,
    handleThreadHeightChange,
    quarterInfo,
    rootUsers,
    getExtraTopMargin,
    transformToTargetCardData,
    isLoading,
  } = useTargetsPage();

  const theme = useTheme();
  const actionsRef = useRef<HTMLDivElement>(null);

  // Loading state
  if (isLoading) {
    return (
      <>
        <TargetsActions
          selectedYear={selectedYear}
          selectedQuarter={selectedQuarter}
          selectedUserUId={selectedUserUId}
          handleYearChange={handleYearChange}
          handleQuarterChange={handleQuarterChange}
          handleUserChange={handleUserChange}
          actionsRef={actionsRef}
        />
        <Box
          sx={{
            width: "100%",
            position: "relative",
            marginTop: screen.isMobile ? "220px" : "100px",
            marginBottom: "100px",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "400px",
          }}
        >
          <CircularProgress size={40} />
        </Box>
      </>
    );
  }

  // Empty state
  if (!rootUsers || rootUsers.length === 0) {
    return (
      <>
        <TargetsActions
          selectedYear={selectedYear}
          selectedQuarter={selectedQuarter}
          selectedUserUId={selectedUserUId}
          handleYearChange={handleYearChange}
          handleQuarterChange={handleQuarterChange}
          handleUserChange={handleUserChange}
          actionsRef={actionsRef}
        />
        <Box
          sx={{
            width: "100%",
            position: "relative",
            marginTop: screen.isMobile ? "220px" : "100px",
            marginBottom: "100px",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "400px",
          }}
        >
          <EmptySection title="targets" />
        </Box>
      </>
    );
  }

  return (
    <>
      <TargetsActions
        selectedYear={selectedYear}
        selectedQuarter={selectedQuarter}
        selectedUserUId={selectedUserUId}
        handleYearChange={handleYearChange}
        handleQuarterChange={handleQuarterChange}
        handleUserChange={handleUserChange}
        actionsRef={actionsRef}
      />
      <Box
        sx={{
          width: "100%",
          position: "relative",
        }}
      >
        <Box
          sx={{
            display: "flex",
            width: "100%",
            flexDirection: "column",
            marginTop: screen.isMobile ? "220px" : "100px",
            marginBottom: "100px",
            gap: 18, // Base gap between nodes
            minWidth: "fit-content",
          }}
        >
          {rootUsers.map((user, index) => {
            // Transform API data for this user
            const targetCardData = transformToTargetCardData(user);

            return (
              <>
                <Box
                  key={`${user.user.cognitoId}-${index}`}
                  position="relative"
                  sx={{
                    marginTop: `${getExtraTopMargin(index)}px`,
                    paddingX: screen.isMobile ? theme.nawy.spacing.xs : "",
                    transition: "margin-top 0.3s ease-in-out", // Smooth transition when spacing changes
                    minWidth: "fit-content",
                  }}
                >
                  {index !== 0 && (
                    <Divider
                      sx={{
                        backgroundColor: theme.nawy.colors.primitives.grey[400],
                        height: "4px",
                        borderRadius: "8px",
                        marginBottom: "32px",
                        width: "100%",
                        minWidth: "100%",
                      }}
                    />
                  )}
                  <AvatarWithConnectors
                    username={user.user.fullName || user.user.name}
                    connectors={[]} // Start with empty connectors, will be populated dynamically
                    hasReports={user.hasReports}
                    cognitoId={user.user.cognitoId}
                    color={user.user.color}
                    startDate={quarterInfo.startDate.toISOString()}
                    endDate={quarterInfo.endDate.toISOString()}
                    onThreadHeightChange={handleThreadHeightChange}
                    targetsData={targetCardData}
                    user={user.user}
                    quarter={selectedQuarter}
                    year={selectedYear}
                  />
                </Box>
              </>
            );
          })}
        </Box>
      </Box>
    </>
  );
};

export default Targets2;
